export type VisibilityCountry = 'CH' | 'ES' | 'FR' | 'IT';

export const PROFILE_POINTS_PER_VIEW = 1;
export const PROFILE_POINTS_TARGET = 50;
export const VALUATION_POINTS_TARGET = 50;

/**
 * Visibility Score weighting between the two scoring signals.
 *
 *   score = VALUATION_WEIGHT × valuation% + PROFILE_WEIGHT × profile%
 *
 * Phone clicks are tracked as a calibration metric under the admin "Seller
 * contacts" sub-card but do not contribute to the gauge.
 */
export const VALUATION_WEIGHT = 0.75;
export const PROFILE_WEIGHT = 0.25;

/** Shared operational rank curve (ES/CH/FR/IT). Index 0 = P1 … index 6 = P7. */
export const RANK_WEIGHTS = [1.0, 0.71, 0.65, 0.41, 0.36, 0.34, 0.31] as const;

export const COUNTRY_MONTHLY_EFFECTIVE_IMPRESSION_TARGETS: Record<
  VisibilityCountry,
  number
> = {
  CH: 49.76,
  ES: 172.11,
  FR: 92.12,
  IT: 44.1,
};

/** Calibration proxy: seller contacts per month at target visibility. */
export const COUNTRY_SELLER_CONTACT_TARGETS_PER_MONTH: Record<
  VisibilityCountry,
  number
> = {
  CH: 1,
  ES: 3,
  FR: 3,
  IT: 3,
};

const HOSTNAME_TO_COUNTRY: Record<string, VisibilityCountry> = {
  'realadvisor.ch': 'CH',
  'realadvisor.es': 'ES',
  'realadvisor.fr': 'FR',
  'realadvisor.it': 'IT',
};

export const hostnameToVisibilityCountry = (
  hostname: string | null | undefined,
): VisibilityCountry | null => {
  if (hostname == null) {
    return null;
  }
  return HOSTNAME_TO_COUNTRY[hostname] ?? null;
};

export const getTargetImpressionsForWindow = (
  country: VisibilityCountry,
  windowDays: number,
) => {
  const monthlyTarget = COUNTRY_MONTHLY_EFFECTIVE_IMPRESSION_TARGETS[country];
  return (monthlyTarget * windowDays) / 30;
};

export const ceilToNearestFive = (score: number) => Math.ceil(score / 5) * 5;

/**
 * Account-size multiplier applied to the visibility score target.
 *
 * Larger agencies need proportionally more raw activity (profile views,
 * valuation impressions, phone clicks) to reach the same percentage score.
 *
 * Tiers (transactions published in the last 24 months):
 *   [0, 15)  → 1.0x   (current baseline)
 *   [15, 30) → 1.5x
 *   [30, 50) → 2.0x
 *   [50, 60) → 2.1x   then +0.1x per additional 10-tx tranche
 *   [60, 70) → 2.2x
 *   …
 */
export const getVisibilitySizeMultiplier = (transactionCount: number) => {
  const n = Math.max(0, Math.floor(transactionCount));
  if (n < 15) {
    return 1.0;
  }
  if (n < 30) {
    return 1.5;
  }
  if (n < 50) {
    return 2.0;
  }
  // Each completed tranche of 10 above 50 adds another 10%.
  // n=50 → 2.1, n=60 → 2.2, n=70 → 2.3 …
  const extraTranches = Math.floor((n - 50) / 10) + 1;
  return 2.0 + extraTranches * 0.1;
};

export const formatVisibilitySizeMultiplier = (multiplier: number) => {
  const rounded = Math.round(multiplier * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}.0x` : `${rounded}x`;
};

export const getWeightForPosition = (position: number) => {
  if (position < 1 || position > 7) {
    return 0;
  }
  return RANK_WEIGHTS[position - 1] ?? 0;
};
